import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from 'src/api';
import { getSuggestionKey } from './suggestionModel';

// Called straight through the api layer rather than the store action of the same name: that action
// writes the comparison into the billing module's own utility, and this is a different bill's.
const NO_OPTIONS = {
  cancelToken: undefined
};
async function fetchComparison(_ref) {
  let billingCycleId = _ref.billingCycleId,
    propertyId = _ref.propertyId,
    utilityId = _ref.utilityId;
  try {
    const resp = await api.Allocations.getAllocationComparison({
      billingCycleId,
      propertyId,
      utilityId
    }, NO_OPTIONS);
    return resp !== null && resp !== void 0 && resp.response ? [getSuggestionKey(propertyId, utilityId), resp.response] : null;
  } catch (_unused) {
    // Without the comparison the service simply stays held for review, which is what it is.
    return null;
  }
}

/**
 * How each service held back for review compares with the cycles before it, keyed by property and
 * utility, and the means to record that somebody has looked. The allocation service holds a table
 * back when the bill rises sharply on the last one; it will not be approved until the comparison
 * has been reviewed, so the comparison has to be readable from wherever the bill is approved.
 */
export function useAllocationComparisons(services) {
  const _useState = useState({}),
    _useState2 = _slicedToArray(_useState, 2),
    comparisons = _useState2[0],
    setComparisons = _useState2[1];
  const _useState3 = useState([]),
    _useState4 = _slicedToArray(_useState3, 2),
    savingKeys = _useState4[0],
    setSavingKeys = _useState4[1];
  const isListening = useRef(true);
  useEffect(() => () => {
    isListening.current = false;
  }, []);

  // The addresses are the dependency; a re-render naming the same ones changes nothing.
  const signature = JSON.stringify((services || []).filter(service => (service === null || service === void 0 ? void 0 : service.propertyId) && (service === null || service === void 0 ? void 0 : service.utilityId) && (service === null || service === void 0 ? void 0 : service.billingCycleId)).map(_ref2 => {
    let billingCycleId = _ref2.billingCycleId,
      propertyId = _ref2.propertyId,
      utilityId = _ref2.utilityId;
    return {
      billingCycleId,
      propertyId,
      utilityId
    };
  }).sort((a, b) => "".concat(a.propertyId).concat(a.utilityId).localeCompare("".concat(b.propertyId).concat(b.utilityId))));
  useEffect(() => {
    const wanted = JSON.parse(signature);
    if (!wanted.length) {
      setComparisons({});
      return;
    }
    Promise.all(wanted.map(fetchComparison)).then(fetched => {
      if (!isListening.current) return;
      setComparisons(Object.fromEntries(fetched.filter(Boolean)));
    });
  }, [signature]);

  /**
   * Records that the comparison has been reviewed, then reads it back: whether a service is still
   * held is the allocation service's to say, not this screen's.
   */
  const review = useCallback(async _ref3 => {
    let billingCycleId = _ref3.billingCycleId,
      propertyId = _ref3.propertyId,
      utilityId = _ref3.utilityId;
    const key = getSuggestionKey(propertyId, utilityId);
    setSavingKeys(current => [...current, key]);
    try {
      const saved = await api.Allocations.postReview({
        billingCycleId,
        isReviewed: true,
        propertyId,
        utilityId
      }, NO_OPTIONS);
      if ((saved === null || saved === void 0 ? void 0 : saved.isSuccessful) !== false) {
        const fetched = await fetchComparison({
          billingCycleId,
          propertyId,
          utilityId
        });
        if (isListening.current && fetched) {
          setComparisons(current => _objectSpread(_objectSpread({}, current), {}, {
            [fetched[0]]: fetched[1]
          }));
        }
      }
    } catch (_unused2) {
      // The checkbox reads the service's answer, so a review that did not save stays unchecked.
    } finally {
      if (isListening.current) setSavingKeys(current => current.filter(saving => saving !== key));
    }
  }, []);
  return {
    comparisons,
    review,
    savingKeys
  };
}